'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';

import { Button, Typography } from 'lighthouse';

import { useCityStore } from '@/stores/city/useCityStore';

import { BATTERY_PRODUCT_CAR_BEST_SELLER_AMPERES, BATTERY_PRODUCT_CAR_CATEGORY_ID } from '@/constants/batteryProduct';
import { allBatteriesHref, CAMPAIGN_PRODUCTS_SECTION_ID, CAMPAIGN_PRODUCTS_SIZE } from '@/constants/batteryToBattery';

import classes from '../../index.module.css';
import Reveal from '../Reveal';
import CampaignProductCard from './CampaignProductCard';

import { getProductsBatteryByVehicleFiltersService } from '@/client/services/core/productBattery/methods';
import { ProductBatteryType } from '@/server/services/core/productBattery/models';
import { ArrowLeft, TicketDiscount } from 'iconsax-react';

type CampaignProductType = ProductBatteryType;

const getCampaignProducts = async (cityId: number) => {
    const response = await getProductsBatteryByVehicleFiltersService({
        cityId,
        categoryIds: [BATTERY_PRODUCT_CAR_CATEGORY_ID],
        amperes: BATTERY_PRODUCT_CAR_BEST_SELLER_AMPERES,
        page: 0,
        size: CAMPAIGN_PRODUCTS_SIZE,
        sort: 'available,desc'
    });

    return response.data.content.filter((product) => product.discountPercentage !== 99);
};

const ProductCardSkeleton = () => (
    <div className='flex w-[268px] shrink-0 snap-start flex-col overflow-hidden rounded-lg bg-white/[0.05] ring-1 ring-white/10 sm:w-auto'>
        <div className='mx-auto my-6 h-28 w-28 animate-pulse rounded-lg bg-white/10' />
        <div className='flex flex-col gap-2 px-4'>
            <div className='h-4 w-3/4 animate-pulse rounded bg-white/10' />
            <div className='h-3 w-1/2 animate-pulse rounded bg-white/10' />
        </div>
        <div className='flex items-center justify-between p-4'>
            <div className='h-5 w-20 animate-pulse rounded bg-white/10' />
            <div className='h-8 w-16 animate-pulse rounded-lg bg-white/10' />
        </div>
    </div>
);

const BatteryProducts = () => {
    const cityId = useCityStore((state) => state.cityId);
    const [retryCount, setRetryCount] = useState(0);
    const [products, setProducts] = useState<CampaignProductType[]>([]);
    const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');

    useEffect(() => {
        let isActive = true;

        setStatus('loading');

        void getCampaignProducts(cityId)
            .then((campaignProducts) => {
                if (isActive) {
                    setProducts(campaignProducts);
                    setStatus('success');
                }
            })
            .catch(() => {
                if (isActive) {
                    setProducts([]);
                    setStatus('error');
                }
            });

        return () => {
            isActive = false;
        };
    }, [cityId, retryCount]);

    const handleRetry = () => setRetryCount((count) => count + 1);

    return (
        <section id={CAMPAIGN_PRODUCTS_SECTION_ID} className={classes.productsSection}>
            <div className='mx-auto w-full max-w-6xl px-5 py-16'>
                <Reveal>
                    <div className='flex items-end justify-between gap-4'>
                        <div>
                            <Typography variant='h5' component='h2' fontWeight={800} className='flex items-center gap-2 text-white'>
                                <TicketDiscount size={26} color='#FBBF24' />
                                خرید باتری خودرو با تخفیف
                            </Typography>
                            <Typography variant='body2' component='p' className='mt-2 text-white/60'>
                                باتری اصل با گارانتی معتبر؛ کد تخفیفت رو روی هر کدوم از این باتری‌ها می‌تونی اعمال کنی.
                            </Typography>
                        </div>
                        <Link
                            href={allBatteriesHref}
                            className='hidden shrink-0 items-center gap-1 text-sm font-bold text-primary transition-colors hover:text-white sm:flex'>
                            مشاهده همه باتری‌ها
                            <ArrowLeft size={16} color='currentcolor' />
                        </Link>
                    </div>
                </Reveal>

                <div
                    className='nice-scroll -mx-5 mt-8 flex snap-x snap-mandatory gap-3 overflow-x-auto px-5 pb-2 sm:mx-0 sm:grid sm:grid-cols-2
                        sm:overflow-visible sm:px-0 lg:grid-cols-4'>
                    {status === 'loading' && [1, 2, 3, 4].map((item) => <ProductCardSkeleton key={item} />)}

                    {status === 'success' && products.map((product) => <CampaignProductCard key={product.uuid} product={product} />)}

                    {status === 'success' && !products.length && (
                        <div className='col-span-full flex w-full flex-col items-center py-10'>
                            <Typography variant='body2' component='p' className='text-white/50'>
                                فعلاً محصولی برای نمایش وجود ندارد.
                            </Typography>
                            <Link href={allBatteriesHref} className='mt-3 text-sm font-bold text-primary'>
                                مشاهده صفحه باتری خودرو
                            </Link>
                        </div>
                    )}

                    {status === 'error' && (
                        <div className='col-span-full flex w-full flex-col items-center py-10'>
                            <Typography variant='body2' component='p' className='text-white/50'>
                                در دریافت لیست باتری‌ها خطایی رخ داد.
                            </Typography>
                            <Button variant='outlined' size='small' onClick={handleRetry} className='mt-4 border-white/30 text-white'>
                                تلاش مجدد
                            </Button>
                        </div>
                    )}
                </div>

                <Link href={allBatteriesHref} className='mt-6 flex items-center justify-center gap-1 text-sm font-bold text-primary sm:hidden'>
                    مشاهده همه باتری‌ها
                    <ArrowLeft size={16} color='currentcolor' />
                </Link>
            </div>
        </section>
    );
};

export default BatteryProducts;
